// Thin, reusable recharts wrapper — the app's FIRST real adoption of the
// installed `recharts` dep (previously only the unused shadcn ui/chart.tsx
// imported it). One place for VYV chart styling: dark background, cyan grid,
// glass tooltip. Generic over the analytics engine's Bucket shape.

import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import type { Bucket, Granularity } from "@/lib/analytics/engine";

const AXIS = "hsl(218 14% 45%)";
const GRID = "hsl(222 18% 20%)";

function labelFor(startMs: number, g: Granularity, locale: string): string {
  const d = new Date(startMs);
  if (g === "month") return d.toLocaleDateString(locale, { month: "short" });
  return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

export interface AnalyticsChartProps {
  buckets: Bucket[];
  color: string;
  granularity: Granularity;
  type?: "line" | "bar";
  locale: string;
  /** value formatter for the tooltip. */
  format?: (v: number) => string;
  height?: number;
}

export function AnalyticsChart({
  buckets,
  color,
  granularity,
  type = "line",
  locale,
  format,
  height = 140,
}: AnalyticsChartProps) {
  const data = useMemo(
    () => buckets.map((b) => ({ x: labelFor(b.start, granularity, locale), v: b.value, key: b.key })),
    [buckets, granularity, locale],
  );

  const tooltip = (
    <Tooltip
      cursor={{ stroke: color, strokeOpacity: 0.25 }}
      contentStyle={{
        background: "hsl(222 18% 12%)",
        border: "1px solid hsl(222 18% 24%)",
        borderRadius: 12,
        fontSize: 12,
      }}
      labelStyle={{ color: "hsl(222 10% 78%)" }}
      formatter={(value: number) => [format ? format(value) : String(Math.round(value)), ""]}
    />
  );

  const common = (
    <>
      <CartesianGrid stroke={GRID} strokeDasharray="2 4" vertical={false} />
      <XAxis dataKey="x" stroke={AXIS} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={16} />
      <YAxis stroke={AXIS} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={28} />
      {tooltip}
    </>
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === "bar" ? (
        <BarChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: -12 }}>
          {common}
          <Bar dataKey="v" fill={color} radius={[3, 3, 0, 0]} />
        </BarChart>
      ) : (
        <LineChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: -12 }}>
          {common}
          <Line
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2}
            dot={{ r: 2.5, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      )}
    </ResponsiveContainer>
  );
}
